// Presentation helpers for a task template's automation state.
//
// The backend (TaskTemplates::EvaluateAutomation) decides whether a template can
// auto-create an EMA task and, when it cannot, returns machine-readable reason
// codes. All wording lives here so copy can change without a backend deploy.

export const AUTOMATIC = 'automatic';
export const INACTIVE = 'inactive';
export const MANUAL = 'manual';

export const ROUTING_RULE = 'routing_rule';

// One sentence, two audiences: the reason list uses the row's own audience when
// it has one and this generic phrasing when it does not. Built as a function so
// the wording cannot drift between the two callers.
const noFaxLine = (audience) => (
  `No fax line is owned by ${audience || 'this template\'s audience'}, `
  + 'and a template only runs on fax lines its audience owns.'
);

// Keep in step with TaskTemplates::EvaluateAutomation::REASON_CODES.
export const REASON_COPY = {
  org_missing_task_auto_upload:
    'Task automation is not enabled for this organization.',
  org_missing_document_classification:
    'AI document classification is not enabled for this organization, so no category is ever assigned.',
  org_missing_upload_and_task_rule:
    'This organization uses auto-upload rules, and none of its active rules create tasks, so no template runs.',
  category_disabled:
    'The trigger category is disabled, so the AI never assigns it.',
  category_not_ai_assignable:
    'The trigger category is not linked to an AI category, so the AI never assigns it.',
  no_fax_line_for_owner: noFaxLine(),
  template_missing_assignee:
    'No provider is assigned, so an automatic task would have no one to assign to.',
};

// When a routing rule drives the template, the category the classifier has to
// assign is the rule's condition, not the template's own trigger. Attributing a
// category failure to the trigger would repeat the mistake this work fixes.
export const ROUTED_REASON_COPY = {
  category_disabled:
    'The category this routing rule matches on is disabled, so the AI never assigns it.',
  category_not_ai_assignable:
    'The category this routing rule matches on is not linked to an AI category, so the AI never assigns it.',
};

const UNKNOWN_REASON = 'This template cannot run automatically. Check its trigger category and audience.';

const MANUAL_USE_FOOTER = 'This template is still available for manual use.';

const RULE_ONLY_PATH_NOTE = 'Reached through an active task routing rule. This template\'s own trigger category is not one the AI can assign, so the rule is the only way it runs.';

const RULE_ONLY_NOTE = 'Selected by an active task routing rule.';

// Ant Design Tag presets. `volcano` rather than the semantic `warning` preset:
// warning renders #faad14 on #fffbe6, about 1.8:1, which is unreadable. volcano
// is #d4380d on #fff2e8, roughly 4.4:1, and still reads as attention rather than
// as an error — an inactive template is misconfigured, not broken.
export const AUTOMATIC_COLOR = 'purple';
export const INACTIVE_COLOR = 'volcano';

// A manual template renders as grey plain text rather than a coloured Tag:
// nothing fires for it, so a badge would overstate what it does.
export const MANUAL_TEXT_COLOR = '#8c8c8c';
export const MANUAL_ONLY_TEXT = 'Manual only';

// "Radiology Report · Imaging Result" when both names are known, otherwise
// whichever one is. Used for the badge and for every category Select on this
// surface so a pair of same-named EMA categories can be told apart.
export const categoryLabel = (emrCategory, aiCategoryName) => {
  if (emrCategory && aiCategoryName && aiCategoryName !== emrCategory) {
    return `${emrCategory} · ${aiCategoryName}`;
  }
  return emrCategory || aiCategoryName || '';
};

// Reason sentences in the order the backend reported them, with the audience
// named for the fax-line reason since the row already carries it.
export const reasonSentences = (reasons = [], { scope, scopeName, routed } = {}) => (
  reasons.map((code) => {
    if (code === 'no_fax_line_for_owner' && scopeName) {
      return noFaxLine(scope === 'Organization' ? `the organization ${scopeName}` : scopeName);
    }
    return (routed && ROUTED_REASON_COPY[code]) || REASON_COPY[code] || UNKNOWN_REASON;
  })
);

// What the Trigger cell should show for one row. Returns null when the row
// predates the automation field, so the caller can fall back to legacy rendering.
export const triggerPresentation = (template = {}) => {
  const { automation } = template;
  if (!automation || !automation.state) return null;

  const label = categoryLabel(template.emr_category, template.ai_category_name);
  const routed = automation.source === ROUTING_RULE;

  if (automation.state === MANUAL) {
    return { kind: MANUAL };
  }

  // A routed template shows "routing rule" rather than its own category, which
  // the runtime ignores on that path. Naming the trigger here would claim the
  // wrong category drives the template.
  if (automation.state === AUTOMATIC) {
    if (routed) {
      return {
        kind: AUTOMATIC,
        color: AUTOMATIC_COLOR,
        text: 'Auto: routing rule',
        tooltip: label ? RULE_ONLY_PATH_NOTE : RULE_ONLY_NOTE,
      };
    }
    return {
      kind: AUTOMATIC,
      color: AUTOMATIC_COLOR,
      text: `Auto: ${label}`,
      tooltip: null,
    };
  }

  const sentences = reasonSentences(automation.inactive_reasons, {
    scope: template.scope,
    scopeName: template.scope_name,
    routed,
  });
  let inactiveText = 'Auto (inactive)';
  if (routed) {
    inactiveText = 'Auto (inactive): routing rule';
  } else if (label) {
    inactiveText = `Auto (inactive): ${label}`;
  }

  return {
    kind: INACTIVE,
    color: INACTIVE_COLOR,
    text: inactiveText,
    tooltip: [...sentences, MANUAL_USE_FOOTER].join(' '),
  };
};

const manualCell = (legacy) => ({
  kind: MANUAL,
  color: MANUAL_TEXT_COLOR,
  text: MANUAL_ONLY_TEXT,
  tooltip: null,
  legacy,
});

// The Trigger column's render contract, resolved here rather than in the column
// so the row-to-badge mapping is testable without mounting the table: `kind`
// picks Tag versus plain text, `tooltip` decides whether a Tooltip wraps it, and
// `legacy` marks the pre-automation fallback path.
//
// `legacyEmrCategory` is the column's dataIndex value, used only when the row
// predates the automation field.
export const triggerCell = (template = {}, legacyEmrCategory = null) => {
  const presentation = triggerPresentation(template);

  // Older payloads carry no automation state; keep the previous rendering so the
  // frontend can deploy before or after the backend.
  if (!presentation) {
    if (!legacyEmrCategory) return manualCell(true);
    return {
      kind: AUTOMATIC,
      color: AUTOMATIC_COLOR,
      text: `Auto: ${legacyEmrCategory}`,
      tooltip: null,
      legacy: true,
    };
  }

  if (presentation.kind === MANUAL) return manualCell(false);

  return {
    kind: presentation.kind,
    color: presentation.color,
    text: presentation.text,
    tooltip: presentation.tooltip || null,
    legacy: false,
  };
};
